/**
 * Pure function that, given transcript speaker data and face detections with
 * face→speaker mappings, classifies each time interval within a scene as
 * `active_speaker`, `non_active_speaker`, or `no_speaker` and attaches the
 * dominant face's detections.
 *
 * The algorithm is a two-pass pipeline:
 *   Pass 1 – resolve `active_speaker` intervals (speaker's own face found).
 *   Pass 2 – fill gaps / unmatched intervals using the nearest resolved
 *            neighbour's face, falling back to most-frequent faceId.
 *
 * @module buildSceneFramingIntervals
 * @see docs/plans/PLAT-6535-active-speaker-detection.md
 */

import { LogManager } from '@src/utility/Logger';

import { MIN_OTHER_CLUSTER_SHARE } from './otherSpeakerCluster';
import {
  filterDetectionsByLabel,
  mostFrequentFaceLabel,
  sliceDetectionsByTimeRange,
} from './speakerIntervalUtils';
import {
  DEFAULT_SCENE_FRAMING_CONFIG,
  type FaceDetection,
  type FaceToSpeakerLabelMapping,
  type SceneFramingConfig,
  type SceneFramingInterval,
  type SceneFramingIntervalType,
  type SpeakerInterval,
} from './types';

const logger = LogManager.getLogger('framing:buildSceneFramingIntervals');

// ---------------------------------------------------------------------------
// Internal types
// ---------------------------------------------------------------------------

/** Intermediate representation before Pass 2 resolution. */
type RawSegment = {
  /** Absolute start time (will be converted to relative at the end). */
  startTime: number;
  /** Absolute end time. */
  endTime: number;
  /** null ⟹ silence / no-speaker segment. */
  speakerLabel: string | null;
  /** Face detections that fall within this segment's time range. */
  bucket: FaceDetection[];
};

type ResolvedSegment = {
  startTime: number;
  endTime: number;
  type: SceneFramingIntervalType;
  faceDetections: FaceDetection[];
  /** The faceId (label) chosen for this segment – used by Pass 2 look-ups. */
  resolvedFaceLabel: string | null;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Build a reverse mapping: speaker-label → Set of face-labels.
 */
function buildReverseFaceToSpeakerLabelMapping(
  speakerMapping: FaceToSpeakerLabelMapping,
): Map<string, Set<string>> {
  const map = new Map<string, Set<string>>();
  for (const [faceLabel, speakerLabel] of Object.entries(speakerMapping)) {
    const existing = map.get(speakerLabel);
    if (existing) existing.add(faceLabel);
    else map.set(speakerLabel, new Set([faceLabel]));
  }
  return map;
}

// ---------------------------------------------------------------------------
// Step 3a – Partition the scene timeline into raw segments
// ---------------------------------------------------------------------------

/**
 * Clip speaker intervals to the scene range, insert silence segments for gaps
 * ≥ `silenceThresholdSec`, and absorb shorter gaps into the preceding speaker
 * interval.
 *
 * Returns segments sorted by startTime covering [sceneStart, sceneEnd).
 */
function partitionTimeline(
  speakerIntervals: readonly SpeakerInterval[],
  sceneStart: number,
  sceneEnd: number,
  silenceThresholdSec: number,
): Array<{ startTime: number; endTime: number; speakerLabel: string | null }> {
  // Clip & filter intervals overlapping with the scene range.
  const clipped: SpeakerInterval[] = [];
  for (const si of speakerIntervals) {
    if (si.endTime <= sceneStart || si.startTime >= sceneEnd) continue;
    clipped.push({
      speakerLabel: si.speakerLabel,
      startTime: Math.max(si.startTime, sceneStart),
      endTime: Math.min(si.endTime, sceneEnd),
    });
  }
  // Already sorted because source intervals come from transcript order.

  const segments: Array<{
    startTime: number;
    endTime: number;
    speakerLabel: string | null;
  }> = [];

  let cursor = sceneStart;

  for (const interval of clipped) {
    const gap = interval.startTime - cursor;
    if (gap >= silenceThresholdSec) {
      // Silence gap large enough → explicit no_speaker segment.
      segments.push({
        startTime: cursor,
        endTime: interval.startTime,
        speakerLabel: null,
      });
    } else if (gap > 0 && segments.length > 0) {
      // Small gap → absorb into the preceding speaker interval.
      segments[segments.length - 1].endTime = interval.startTime;
    } else if (gap > 0 && segments.length === 0) {
      // Leading gap below threshold — extend the upcoming speaker interval
      // back to cursor so the scene is fully covered (no orphan range).
      segments.push({
        startTime: cursor,
        endTime: interval.endTime,
        speakerLabel: interval.speakerLabel,
      });
      cursor = interval.endTime;
      continue;
    }

    segments.push({
      startTime: interval.startTime,
      endTime: interval.endTime,
      speakerLabel: interval.speakerLabel,
    });
    cursor = interval.endTime;
  }

  // Trailing silence after the last speaker interval.
  const trailing = sceneEnd - cursor;
  if (trailing >= silenceThresholdSec) {
    segments.push({
      startTime: cursor,
      endTime: sceneEnd,
      speakerLabel: null,
    });
  } else if (trailing > 0 && segments.length > 0) {
    // Absorb small trailing gap.
    segments[segments.length - 1].endTime = sceneEnd;
  } else if (trailing > 0 && segments.length === 0) {
    // The entire scene is silence.
    segments.push({
      startTime: sceneStart,
      endTime: sceneEnd,
      speakerLabel: null,
    });
  }

  return segments;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Builds an array of `SceneFramingInterval`s for a single scene.
 *
 * @param speakerIntervals  Speaker intervals from the transcript (media time).
 * @param faceDetections    All face detections, **sorted by `time` ascending**.
 * @param speakerMapping    Face-label → speaker-label mapping.
 * @param sceneRange        Absolute start/end of the scene in timeline time.
 * @param config            Optional configuration overrides.
 * @returns Sorted array of non-overlapping framing intervals with relative
 *          times (0 = scene start).
 */
export function buildSceneFramingIntervals(
  speakerIntervals: readonly SpeakerInterval[],
  faceDetections: readonly FaceDetection[],
  speakerMapping: FaceToSpeakerLabelMapping,
  sceneRange: { start: number; end: number },
  config: SceneFramingConfig = DEFAULT_SCENE_FRAMING_CONFIG,
): SceneFramingInterval[] {
  const { start: sceneStart, end: sceneEnd } = sceneRange;
  if (sceneEnd <= sceneStart) return [];

  const reverseSpeakerMap =
    buildReverseFaceToSpeakerLabelMapping(speakerMapping);
  const isTwoPerson =
    new Set(
      speakerIntervals
        .map(si => si.speakerLabel)
        .filter(label => label !== 'unknown'),
    ).size === 2;

  logger.debug(
    `Scene [${sceneStart.toFixed(2)}s–${sceneEnd.toFixed(2)}s] | ` +
      `speakerIntervals: ${speakerIntervals.length}, ` +
      `faceDetections: ${faceDetections.length}, ` +
      `speakerMapping: ${JSON.stringify(speakerMapping)}, ` +
      `reverseSpeakerMap: ${[...reverseSpeakerMap.entries()].map(([k, v]) => `${k}→[${[...v].join(',')}]`).join('; ')}`,
  );

  // 3a – partition
  const rawPartitions = partitionTimeline(
    speakerIntervals,
    sceneStart,
    sceneEnd,
    config.silenceThresholdSec,
  );

  if (rawPartitions.length === 0) return [];

  logger.debug(
    `3a partition → ${rawPartitions.length} segments: ` +
      rawPartitions
        .map(
          s =>
            `[${s.startTime.toFixed(2)}–${s.endTime.toFixed(2)}s ` +
            `${s.speakerLabel ?? 'SILENCE'}]`,
        )
        .join(', '),
  );

  // 3b – bucket face detections into each segment
  const rawSegments: RawSegment[] = rawPartitions.map(seg => ({
    ...seg,
    bucket: sliceDetectionsByTimeRange(
      faceDetections,
      seg.startTime,
      seg.endTime,
    ),
  }));

  logger.debug(
    `3b buckets → ` +
      rawSegments
        .map(
          s =>
            `[${s.startTime.toFixed(2)}–${s.endTime.toFixed(2)}s ` +
            `${s.speakerLabel ?? 'SILENCE'}: ${s.bucket.length} detections, ` +
            `faces: ${[...new Set(s.bucket.map(d => d.label))].join(',') || 'none'}]`,
        )
        .join(', '),
  );

  // 3c Pass 1 – resolve active_speaker segments
  const resolved: ResolvedSegment[] = rawSegments.map(seg => {
    if (seg.speakerLabel == null) {
      // Silence segment – deferred to Pass 2.
      const segType: SceneFramingIntervalType = 'no_speaker';
      return {
        startTime: seg.startTime,
        endTime: seg.endTime,
        type: segType,
        faceDetections: [],
        resolvedFaceLabel: null,
      };
    }

    // Check if the speaker's own face appears in the bucket.
    const faceLabelsForSpeaker = reverseSpeakerMap.get(seg.speakerLabel);
    if (faceLabelsForSpeaker) {
      for (const fl of faceLabelsForSpeaker) {
        const matched = filterDetectionsByLabel(seg.bucket, fl);
        if (matched.length > 0) {
          const activeType: SceneFramingIntervalType = 'active_speaker';
          return {
            startTime: seg.startTime,
            endTime: seg.endTime,
            type: activeType,
            faceDetections: matched,
            resolvedFaceLabel: fl,
          };
        }
      }
    }

    // Speaker's face not found – mark unresolved for Pass 2.
    logger.debug(
      `  Pass1 UNRESOLVED [${seg.startTime.toFixed(2)}–${seg.endTime.toFixed(2)}s] ` +
        `speaker=${seg.speakerLabel}, bucket faces=[${[...new Set(seg.bucket.map(d => d.label))].join(',')}] ` +
        `→ face not matched, deferring to Pass 2`,
    );
    const nonActiveType: SceneFramingIntervalType = 'non_active_speaker';
    return {
      startTime: seg.startTime,
      endTime: seg.endTime,
      type: nonActiveType,
      faceDetections: [],
      resolvedFaceLabel: null,
    };
  });

  logger.debug(
    `3c Pass1 summary → ` +
      resolved
        .map(
          s =>
            `[${s.startTime.toFixed(2)}–${s.endTime.toFixed(2)}s ` +
            `${s.type} face=${s.resolvedFaceLabel ?? 'PENDING'} ` +
            `(${s.faceDetections.length} dets)]`,
        )
        .join(', '),
  );

  // 3c Pass 2 – fill unresolved segments from nearest active_speaker neighbour
  for (let i = 0; i < resolved.length; i++) {
    const seg = resolved[i];
    if (seg.resolvedFaceLabel !== null) continue; // already resolved
    if (rawSegments[i].bucket.length === 0) continue; // nothing to pick from

    const bucket = rawSegments[i].bucket;

    // Two people and the speaker's face isn't mapped: they're whoever isn't
    // the other speaker. A neighbour's face is usually the other speaker's —
    // the listener (PLAT-11992).
    const speakerLabel = rawSegments[i].speakerLabel;
    if (isTwoPerson && speakerLabel != null) {
      const notOther = bucket.filter(
        d =>
          speakerMapping[d.label] == null ||
          speakerMapping[d.label] === speakerLabel,
      );
      const label = mostFrequentFaceLabel(notOther);
      const matched = label ? filterDetectionsByLabel(bucket, label) : [];
      if (label && matched.length / bucket.length >= MIN_OTHER_CLUSTER_SHARE) {
        seg.faceDetections = matched;
        seg.resolvedFaceLabel = label;
        logger.debug(
          `  Pass2 [${seg.startTime.toFixed(2)}–${seg.endTime.toFixed(2)}s] ${seg.type}: ` +
            `resolved by excluding the other speaker, face=${label} (${matched.length} dets)`,
        );
        continue;
      }
    }

    // Look for nearest resolved active_speaker neighbours (prev then next).
    let neighbourFaceLabel: string | null = null;

    // Search previous.
    for (let j = i - 1; j >= 0; j--) {
      if (
        resolved[j].type === 'active_speaker' &&
        resolved[j].resolvedFaceLabel
      ) {
        neighbourFaceLabel = resolved[j].resolvedFaceLabel;
        break;
      }
    }

    // If neighbour found, check it has detections in this bucket.
    if (neighbourFaceLabel) {
      const matched = filterDetectionsByLabel(bucket, neighbourFaceLabel);
      if (matched.length > 0) {
        seg.faceDetections = matched;
        seg.resolvedFaceLabel = neighbourFaceLabel;
        logger.debug(
          `  Pass2 [${seg.startTime.toFixed(2)}–${seg.endTime.toFixed(2)}s] ${seg.type}: ` +
            `resolved via PREV neighbour face=${neighbourFaceLabel} (${matched.length} dets)`,
        );
        continue;
      }
    }

    // Search next neighbour.
    let nextNeighbourFaceLabel: string | null = null;
    for (let j = i + 1; j < resolved.length; j++) {
      if (
        resolved[j].type === 'active_speaker' &&
        resolved[j].resolvedFaceLabel
      ) {
        nextNeighbourFaceLabel = resolved[j].resolvedFaceLabel;
        break;
      }
    }

    if (nextNeighbourFaceLabel) {
      const matched = filterDetectionsByLabel(bucket, nextNeighbourFaceLabel);
      if (matched.length > 0) {
        seg.faceDetections = matched;
        seg.resolvedFaceLabel = nextNeighbourFaceLabel;
        logger.debug(
          `  Pass2 [${seg.startTime.toFixed(2)}–${seg.endTime.toFixed(2)}s] ${seg.type}: ` +
            `resolved via NEXT neighbour face=${nextNeighbourFaceLabel} (${matched.length} dets)`,
        );
        continue;
      }
    }

    // Fallback: most frequent faceId in the bucket.
    const bestLabel = mostFrequentFaceLabel(bucket);
    if (bestLabel) {
      seg.faceDetections = filterDetectionsByLabel(bucket, bestLabel);
      seg.resolvedFaceLabel = bestLabel;
      logger.debug(
        `  Pass2 [${seg.startTime.toFixed(2)}–${seg.endTime.toFixed(2)}s] ${seg.type}: ` +
          `resolved via MOST-FREQUENT fallback face=${bestLabel} (${seg.faceDetections.length} dets)`,
      );
    } else {
      logger.debug(
        `  Pass2 [${seg.startTime.toFixed(2)}–${seg.endTime.toFixed(2)}s] ${seg.type}: ` +
          `UNRESOLVED – no neighbour face in bucket, no detections to pick from`,
      );
    }
  }

  // Convert to SceneFramingInterval with relative times.
  const result = resolved.map(seg => ({
    startRelativeTime: seg.startTime - sceneStart,
    endRelativeTime: seg.endTime - sceneStart,
    type: seg.type,
    faceDetections: seg.faceDetections,
  }));

  logger.debug(
    `Final intervals (${result.length}): ` +
      result
        .map(
          r =>
            `[${r.startRelativeTime.toFixed(2)}–${r.endRelativeTime.toFixed(2)}s ` +
            `${r.type} (${r.faceDetections.length} dets` +
            `${r.faceDetections.length > 0 ? `, face=${r.faceDetections[0].label}` : ''})]`,
        )
        .join(', '),
  );

  return result;
}

// ---------------------------------------------------------------------------
// Phase 1 helper – pick the longest interval's detections
// ---------------------------------------------------------------------------

/**
 * Returns the `faceDetections` from the most authoritative interval that
 * carries detections. Priority: `active_speaker` (known face) >
 * `non_active_speaker` (guessed) > `no_speaker`; duration tie-breaks within
 * the same type. Returns `[]` when no interval carries detections.
 */
const TYPE_PRIORITY: Record<SceneFramingIntervalType, number> = {
  active_speaker: 3,
  non_active_speaker: 2,
  no_speaker: 1,
};

export function getLongestIntervalDetections(
  intervals: readonly SceneFramingInterval[],
): FaceDetection[] {
  if (intervals.length === 0) return [];

  let best: SceneFramingInterval | null = null;
  let bestPriority = -1;
  let bestDuration = -1;

  for (const interval of intervals) {
    if (interval.faceDetections.length === 0) continue;
    const priority = TYPE_PRIORITY[interval.type];
    const duration = interval.endRelativeTime - interval.startRelativeTime;
    if (
      priority > bestPriority ||
      (priority === bestPriority && duration > bestDuration)
    ) {
      best = interval;
      bestPriority = priority;
      bestDuration = duration;
    }
  }

  if (best) {
    logger.debug(
      `getLongestIntervalDetections → picked [${best.startRelativeTime.toFixed(2)}–${best.endRelativeTime.toFixed(2)}s] ` +
        `${best.type}, duration=${bestDuration.toFixed(2)}s, ` +
        `face=${best.faceDetections[0]?.label ?? '?'}, ${best.faceDetections.length} detections`,
    );
  } else {
    logger.debug(
      `getLongestIntervalDetections → no interval with detections found (${intervals.length} intervals checked)`,
    );
  }

  return best ? best.faceDetections : [];
}
